import { flow, head, isEmpty, set } from 'lodash/fp';
import { useEffect, useState } from 'react';

import { FRAGMENTS } from '../../../../models/table-of-contents/constants';
import { GET_INSURANCE_TYPE_QUERY } from '../../queries';
import { TableOfContentsList } from '../../../../types/__codegen__/sanity/graphql';
import getCmsData from '../../../../cms/utils/get-cms-data';
import getProductsByType from '../../../../../../pages/utils/get-products-by-type';
import getTableOfContentsListByIdentifierQuery from '../../../../models/table-of-contents/utils/get-table-of-contents-list-by-identifier-query';
import makeClientQuery from '../../../../cms/client/utils/make-client-query';
import useGetLatestArticles from '../../../blog-detail/hooks/use-get-latest-articles';
import useInsuranceTypeQuery from '../use-insurance-type-query';
import type { ExtendedCmsInsuranceTypeContent, ProductsByType } from '../../types';

type UseGetInsuranceType = {
  insuranceType: string;
  shouldSkip: boolean;
};

const AMOUNT_OF_ARTICLES = 3;

const useGetInsuranceType = ({ insuranceType, shouldSkip }: UseGetInsuranceType) => {
  const [products, setProducts] = useState<ProductsByType[] | null>(null);
  const [announcement, setAnnouncement] = useState<TableOfContentsList | null>(null);
  const [allInsuranceTypeData, setAllInsuranceTypeData] = useState<ExtendedCmsInsuranceTypeContent | null>(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingAnnouncement, setIsLoadingAnnouncement] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { data: insuranceTypeData, loading: isLoadingInsuranceType } = useInsuranceTypeQuery(GET_INSURANCE_TYPE_QUERY, {
    skip: shouldSkip || !isEmpty(allInsuranceTypeData),
    variables: { id: insuranceType },
  });
  const { data: articles, isLoading: isLoadingArticles } = useGetLatestArticles({
    options: { skip: shouldSkip, variables: { limit: AMOUNT_OF_ARTICLES, type: insuranceType } },
  });

  useEffect(() => {
    if (shouldSkip) {
      return;
    }

    const fetchProductsAndAnnouncement = async () => {
      setIsLoadingProducts(true);
      setIsLoadingAnnouncement(true);
      try {
        const productsByType = await getProductsByType({ client: makeClientQuery, type: insuranceType });
        const announcementData = await getCmsData(
          getTableOfContentsListByIdentifierQuery(FRAGMENTS.ANNOUNCEMENT_BY_INSURANCE_TYPE),
          {
            variables: { identifier: `${insuranceType}-announcement` },
          },
        );
        setProducts(productsByType);
        setAnnouncement(head(announcementData?.allTableOfContentsList) ?? {});
      } catch (error) {
        setHasError(true);
      } finally {
        setIsLoadingProducts(false);
        setIsLoadingAnnouncement(false);
      }
    };
    fetchProductsAndAnnouncement();
  }, [insuranceType]);

  useEffect(() => {
    if (insuranceTypeData && products && articles && announcement) {
      const insuranceDataToSet = flow(
        set('articles', articles),
        set('productsByType', products),
        set('announcement', announcement),
      )(insuranceTypeData) as ExtendedCmsInsuranceTypeContent;
      setAllInsuranceTypeData(insuranceDataToSet);
    }
  }, [products, insuranceTypeData, articles, announcement]);

  const isLoading = isLoadingInsuranceType || isLoadingProducts || isLoadingArticles || isLoadingAnnouncement;

  return { data: allInsuranceTypeData, hasError, isLoading };
};

export default useGetInsuranceType;
